import Colors from '@constants/Colors';
import { alpha, createTheme, darken } from '@mui/material';
import { frFR } from '@mui/x-date-pickers/locales';

import { baseComponents } from './theme/base/component';
import { baseTypography } from './theme/base/typography';
import { darkColorPalette, lightColorPalette } from './theme/colorPalettes';

// Add muted color to the palette
export const lightTheme = createTheme(
    {
        typography: {
            ...baseTypography,
            body1: {
                color: '#242333 !important',
            },
            displayXXSmall: {
                fontFamily: 'var(--font-family-Brand)',
                fontSize: 'var(--font-size-xl)',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'var(--font-line-height-lg)',
                color: '#242333 !important',
            },
        },
        palette: {
            mode: 'light',
            background: {
                default: '#f9f9f9',
                paper: '#ffffff',
            },
            action: {
                hover: lightColorPalette.primaryPurple30.main,
            },
            ...lightColorPalette
        },
        components: {
            ...baseComponents,
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: '#ffffff',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    tag: {
                        backgroundColor: `${Colors.NEUTRAL_BLACK} !important`,
                        color: `${Colors.WHITE} !important`,
                        borderColor: `${Colors.NEUTRAL_BLACK} !important`,
                    },
                    inputRoot: {
                        color: '#242333',
                        borderRadius: '8px',
                        background: '#f9f9f9',
                        backdropFilter: 'blur(2.799999952316284px)',
                        padding: '14px 16px !important',
                        '& fieldset': {
                            border: 'none',
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '0 !important',
                        },
                    },
                },
                defaultProps: {
                    componentsProps: {
                        paper: {
                            sx: {
                                '& .MuiAutocomplete-listbox': {
                                    '& .MuiAutocomplete-option': {
                                        color: '#242333',
                                    },
                                },
                            },
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: '#242333',
                        '&.Mui-focused': {
                            color: '#fff',
                        },
                        '&.Mui-error': {
                            color: Colors.ERROR_RED,
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    label: {
                        fontWeight: 700,
                        color: `${Colors.WHITE} !important`,
                    },
                    deleteIcon: {
                        color: `${Colors.WHITE} !important`,
                        '&:hover': {
                            color: `${Colors.WHITE} !important`,
                        }
                    }
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        '&.MuiSwitch-sizeMedium': {
                            padding: 6,
                            '& .MuiSwitch-thumb': {
                                width: 20,
                                height: 20,
                            },
                            '& + .MuiSwitch-track': {
                                height: 24,
                            },
                            marginRight: 8,
                            marginLeft: 8,
                        },
                        '&.MuiSwitch-sizeSmall': {
                            padding: 2,
                            marginRight: 8,
                            marginLeft: 8,
                            '& .MuiSwitch-thumb': {
                                width: 16,
                                height: 16,
                            },
                            '& + .MuiSwitch-track': {
                                height: 16,
                            },
                        },
                    },
                    switchBase: {
                        // Overrides for medium size
                        // Controls default (unchecked) color for the thumb
                        color: Colors.WHITE,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: Colors.WHITE,
                        },
                    },
                    track: {
                        // pill border radius
                        borderRadius: '24px',
                        // Controls default (unchecked) color for the track
                        opacity: 0.5,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: `1`,
                            backgroundColor: `${Colors.PRIMARY}`,
                        },
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        transform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "Lemon-Milk-Pro-Regular",
                        lineHeight: '20px',
                        marginBottom: '4px',
                        color: "#242333",
                        '&.Mui-error': {
                            color: Colors.ERROR_RED,
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: Colors.NEUTRAL_LIGHT_GRAY,
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            lineHeight: '20px',
                            backgroundColor: '#F9F9F9',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#F5F5F5',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                            '&.Mui-error fieldset': {
                                border: 'none',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '14px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            lineHeight: '20px',
                            color: Colors.NEUTRAL_BLACK,
                            '&::placeholder': {
                                color: '#939393',
                                opacity: 1,
                            },
                            '&.Mui-disabled': {
                                WebkitTextFillColor: '#939393',
                                color: '#939393',
                            },
                        },
                        '& .MuiInputBase-root.Mui-error .MuiInputBase-input::placeholder': {
                            color: Colors.ERROR_RED,
                            opacity: 1,
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        lineHeight: '20px',
                        backgroundColor: '#F9F9F9',
                        '& fieldset': {
                            border: 'none',
                        },
                        '&:hover fieldset': {
                            border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                            border: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#F5F5F5',
                            '& fieldset': {
                                border: 'none',
                            },
                        },
                        '&.Mui-error fieldset': {
                            border: 'none',
                        },
                    },
                    input: {
                        padding: '14px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        lineHeight: '20px',
                        color: Colors.NEUTRAL_BLACK,
                        '&::placeholder': {
                            color: '#939393',
                            opacity: 1,
                        },
                        '&.Mui-disabled': {
                            WebkitTextFillColor: '#939393',
                            color: '#939393',
                        },
                    },
                },
            },
            MuiButton: {
                ...baseComponents.MuiButton,
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: '24px',
                        fontWeight: 'normal',
                        color: Colors.FLOATING_BAR_TEXT,
                    },
                },
                variants: [
                    ...(baseComponents.MuiButton?.variants || []),
                    {
                        props: { variant: 'outlined', color: 'neutralWhite' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
                            borderColor: Colors.FLOATING_BAR_BUTTON_BACKGROUND,
                            color: palette.primary.main,
                            fontWeight: 700,
                            '&:hover': {
                                borderColor: Colors.FLOATING_BAR_BUTTON_BACKGROUND,
                                backgroundColor: Colors.FLOATING_BAR_BUTTON_BACKGROUND,
                                color: palette.primary.main,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            color: palette.common.white,
                            fontWeight: 700
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.primary.main,
                            color: Colors.WHITE,
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: Colors.SECONDARY,
                                color: Colors.WHITE,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'error' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.error.main,
                            color: Colors.WHITE,
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: darken(palette.error.main, 0.2),
                                color: Colors.WHITE,
                            },
                        }),
                    },
                ],
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '50%',
                    },
                },
                variants: [
                    {
                        props: { variant: 'contained', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.primary.main,
                            color: palette.neutralWhite.main,
                            '&:hover': {
                                backgroundColor: palette.primaryPurple.main,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: palette.action.disabledBackground,
                                color: palette.action.disabled,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.secondary.main,
                            color: palette.neutralWhite.main,
                            '&:hover': {
                                backgroundColor: darken(palette.secondary.main, 0.12),
                            },
                            '&.Mui-disabled': {
                                backgroundColor: palette.action.disabledBackground,
                                color: palette.action.disabled,
                            },
                        }),
                    },
                    {
                        props: { variant: 'outlined', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            border: `1px solid ${palette.primary.main}`,
                            color: palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(palette.primary.main, 0.08),
                            },
                        }),
                    },
                    {
                        props: { variant: 'outlined', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            border: `px solid ${alpha(palette.secondary.main, 0.5)}`,
                            color: palette.secondary.main,
                            '&:hover': {
                                borderColor: palette.secondary.main,
                                backgroundColor: palette.secondary.main,
                                color: palette.primary.main,
                            },
                        }),
                    },
                    {
                        props: { variant: 'text', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            color: palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(palette.primary.main, 0.08),
                            },
                        }),
                    },
                    {
                        props: { variant: 'text', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            color: palette.secondary.main,
                            '&:hover': {
                                backgroundColor: palette.secondary.main,
                                color: palette.primary.main,
                            },
                        }),
                    },
                ],
            },
            MuiPaper: {
                // overrides box shadow for paper component
                styleOverrides: {
                    root: {
                        boxShadow: 'none'
                    }
                }
            }
        },
    },
    frFR,
);

export const darkTheme = createTheme(
    {
        typography: baseTypography,
        palette: {
            mode: 'dark',
            background: {
                default: '#222426', // Dark background color
                paper: '#222426', // Paper background color
            },
            action: {
                hover: "#16133B", // Hover color for dark theme
            },
            ...darkColorPalette
        },
        components: {
            ...baseComponents,
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: '#000',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    tag: {
                        border: `1px solid ${darkColorPalette.primary.main}`,
                        backgroundColor: alpha(darkColorPalette.primary.main, 0.3),
                    },
                    inputRoot: {
                        color: '#cbcbcb',
                        borderRadius: '8px',
                        background: '#282c30',
                        backdropFilter: 'blur(2.799999952316284px)',
                        padding: '14px 16px !important',
                        '& fieldset': {
                            border: 'none',
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '0 !important',
                        },
                    },

                },
                defaultProps: {
                    componentsProps: {
                        paper: {
                            sx: {
                                '& .MuiAutocomplete-listbox': {
                                    '& .MuiAutocomplete-option': {
                                        color: '#cbcbcb',
                                    },
                                },
                            },
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: '#cbcbcb',
                        '&.Mui-focused': {
                            color: '#fff',
                        },
                        '&.Mui-error': {
                            color: Colors.ERROR_RED,
                        },
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        '&.MuiSwitch-sizeMedium': {
                            padding: 6,
                            '& .MuiSwitch-thumb': {
                                width: 20,
                                height: 20,
                            },
                            '& + .MuiSwitch-track': {
                                height: 24,
                            },
                            marginRight: 8,
                            marginLeft: 8,
                        },
                        '&.MuiSwitch-sizeSmall': {
                            padding: 2,
                            marginRight: 8,
                            marginLeft: 8,
                            '& .MuiSwitch-thumb': {
                                width: 16,
                                height: 16,
                            },
                            '& + .MuiSwitch-track': {
                                height: 16,
                            },
                        },
                    },
                    switchBase: {
                        // Overrides for medium size
                        // Controls default (unchecked) color for the thumb
                        color: Colors.WHITE,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: Colors.WHITE,
                        },
                    },
                    track: {
                        // pill border radius
                        borderRadius: '24px',
                        // Controls default (unchecked) color for the track
                        opacity: 0.5,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: `1`,
                            backgroundColor: `${Colors.PRIMARY}`,
                        },
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        transform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "Lemon-Milk-Pro-Regular",
                        lineHeight: '28px',
                        marginBottom: '4px',
                        color: '#FFFFFF',
                        '&.Mui-error': {
                            color: Colors.ERROR_RED,
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            lineHeight: '20px',
                            backgroundColor: '#282C30',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#424242',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                            '&.Mui-error fieldset': {
                                border: 'none',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '14px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            lineHeight: '20px',
                            color: '#FFFFFF',
                            '&::placeholder': {
                                color: '#939393',
                                opacity: 1,
                            },
                            '&.Mui-disabled': {
                                WebkitTextFillColor: '#939393',
                                color: '#939393',
                            },
                        },
                        '& .MuiInputBase-root.Mui-error .MuiInputBase-input::placeholder': {
                            color: Colors.ERROR_RED,
                            opacity: 1,
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        lineHeight: '20px',
                        backgroundColor: '#282C30',
                        '& fieldset': {
                            border: 'none',
                        },
                        '&:hover fieldset': {
                            border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                            border: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#424242',
                            '& fieldset': {
                                border: 'none',
                            },
                        },
                        '&.Mui-error fieldset': {
                            border: 'none',
                        },
                    },
                    input: {
                        padding: '14px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        lineHeight: '20px',
                        color: '#FFFFFF',
                        '&::placeholder': {
                            color: '#939393',
                            opacity: 1,
                        },
                        '&.Mui-disabled': {
                            WebkitTextFillColor: '#939393',
                            color: '#939393',
                        },
                    },
                },
            },
            MuiButton: {
                ...baseComponents.MuiButton,
                variants: [
                    ...(baseComponents.MuiButton?.variants || []),
                    {
                        props: { variant: 'outlined', color: 'muted' },
                        style: ({ theme: { palette } }) => ({
                            borderColor: palette.muted.main,
                            color: palette.muted.main,
                            '&:hover': {
                                borderColor: palette.muted.main,
                                backgroundColor: palette.muted.main,
                                color: palette.muted.contrastText,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'neutralWhite' },
                        style: ({ theme: { palette } }) => ({
                            borderColor: palette.neutralWhite.main,
                            color: palette.primary.main,
                            fontWeight: 700,
                            '&:hover': {
                                borderColor: palette.neutralWhite.main,
                                backgroundColor: palette.neutralWhite.main,
                                color: palette.neutralWhite.contrastText,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'error' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.error.main,
                            color: Colors.WHITE,
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: darken(palette.error.main, 0.2),
                                color: Colors.WHITE,
                            },
                        }),
                    },
                ],
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '50%',
                    },
                },
                variants: [
                    {
                        props: { variant: 'contained', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.primary.main,
                            color: palette.neutralWhite.main,
                            '&:hover': {
                                backgroundColor: darken(palette.primary.main, 0.2),
                            },
                            '&.Mui-disabled': {
                                backgroundColor: palette.action.disabledBackground,
                                color: palette.action.disabled,
                            },
                        }),
                    },
                    {
                        props: { variant: 'contained', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            backgroundColor: palette.secondary.main,
                            color: palette.neutralWhite.main,
                            '&:hover': {
                                backgroundColor: darken(palette.secondary.main, 0.2),
                            },
                            '&.Mui-disabled': {
                                backgroundColor: palette.action.disabledBackground,
                                color: palette.action.disabled,
                            },
                        }),
                    },
                    {
                        props: { variant: 'outlined', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            border: `1px solid ${palette.primary.main}`,
                            color: palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(palette.primary.main, 0.16),
                            },
                        }),
                    },
                    {
                        props: { variant: 'outlined', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            border: `1px solid ${alpha(palette.secondary.main, 0.5)}`,
                            color: palette.secondary.main,
                            '&:hover': {
                                borderColor: palette.secondary.main,
                                backgroundColor: alpha(palette.secondary.main, 0.16),
                            },
                        }),
                    },
                    {
                        props: { variant: 'text', color: 'primary' },
                        style: ({ theme: { palette } }) => ({
                            color: palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(palette.primary.main, 0.16),
                            },
                        }),
                    },
                    {
                        props: { variant: 'text', color: 'secondary' },
                        style: ({ theme: { palette } }) => ({
                            color: palette.secondary.main,
                            '&:hover': {
                                backgroundColor: palette.secondary.main,
                                color: palette.primary.main,
                            },
                        }),
                    },
                ],
            },
        },
    },
    frFR,
);
